/**
 * nav-hover-preview.js
 *
 * Crossfades a stack of collection banners inside a mega menu as the visitor
 * hovers the menu links.
 *
 * Markup contract (produced by snippets/nav-hover-preview.liquid and the
 * matching edits in snippets/navigation.liquid):
 *
 *   [data-menu-list-id="X"]        the menu column container
 *   [data-nav-preview-handle="h"]  a link inside that container
 *   [data-nav-preview-panel="X"]   the panel holding the stacked images
 *   [data-nav-preview-for="h"]     one image inside the panel
 *   [data-nav-preview-default="h"] the image shown when nothing is hovered
 *
 * All listeners are delegated on document, so menus injected later (section
 * rendering, theme editor reloads) work without re-initialising.
 */
(function () {
  'use strict';

  if (window.navHoverPreviewLoaded) return;
  window.navHoverPreviewLoaded = true;

  // Ignore cursor sweeps that pass straight over a link. Long enough to stop
  // the panel thrashing, short enough to feel instant on a deliberate hover.
  var HOVER_INTENT_MS = 60;

  var hoverTimer = null;
  var pendingFrame = null;
  var preloadedPanels = new WeakSet();
  var panelCache = new Map();

  var requestIdle =
    window.requestIdleCallback ||
    function (callback) {
      return window.setTimeout(callback, 150);
    };

  /**
   * Find the panel belonging to a menu container, memoised per menu id.
   */
  function getPanel(container) {
    var id = container.getAttribute('data-menu-list-id');
    if (!id) return null;

    if (!panelCache.has(id)) {
      panelCache.set(id, document.querySelector('[data-nav-preview-panel="' + id + '"]'));
    }

    var panel = panelCache.get(id);

    // The menu may have been re-rendered since we cached it.
    if (panel && !panel.isConnected) {
      panel = document.querySelector('[data-nav-preview-panel="' + id + '"]');
      panelCache.set(id, panel);
    }

    return panel;
  }

  /**
   * Decode the panel's images ahead of time so swaps never show a blank frame.
   *
   * Runs one image at a time during idle moments, and only after a short delay,
   * so decoding never competes with the menu's own open transition.
   */
  function preloadPanel(panel) {
    if (!panel || preloadedPanels.has(panel)) return;
    preloadedPanels.add(panel);

    var images = Array.prototype.slice.call(panel.querySelectorAll('img'));

    function decodeNext() {
      var image = images.shift();
      if (!image) return;

      image.loading = 'eager';

      var scheduleNext = function () {
        requestIdle(decodeNext);
      };

      if (typeof image.decode === 'function') {
        image.decode().then(scheduleNext, scheduleNext);
      } else {
        scheduleNext();
      }
    }

    window.setTimeout(function () {
      requestIdle(decodeNext);
    }, 250);
  }

  /**
   * Show the banner for `handle`, batching the class change into one paint.
   */
  function activate(panel, handle) {
    if (!panel || !handle) return;

    var target = panel.querySelector('[data-nav-preview-for="' + handle + '"]');
    if (!target || target.classList.contains('is-active')) return;

    if (pendingFrame) cancelAnimationFrame(pendingFrame);

    pendingFrame = requestAnimationFrame(function () {
      pendingFrame = null;

      var current = panel.querySelector('.nav-preview__item.is-active');
      if (current === target) return;

      if (current) {
        current.classList.remove('is-active');
        current.setAttribute('aria-hidden', 'true');
      }

      target.classList.add('is-active');
      target.setAttribute('aria-hidden', 'false');
    });
  }

  /**
   * @param {Element} element   the element under the pointer or receiving focus
   * @param {boolean} immediate skip the hover-intent delay (keyboard use)
   */
  function handleTarget(element, immediate) {
    var container = element.closest('[data-menu-list-id]');
    if (!container) return;

    var panel = getPanel(container);
    if (!panel) return;

    // Start warming images as soon as the menu is entered, even if the pointer
    // is not yet over a link with its own banner.
    preloadPanel(panel);

    var link = element.closest('[data-nav-preview-handle]');
    if (!link) return;

    var handle = link.getAttribute('data-nav-preview-handle');
    if (!handle) return;

    if (hoverTimer) clearTimeout(hoverTimer);

    if (immediate) {
      activate(panel, handle);
    } else {
      hoverTimer = window.setTimeout(function () {
        activate(panel, handle);
      }, HOVER_INTENT_MS);
    }
  }

  document.addEventListener(
    'pointerover',
    function (event) {
      if (event.target instanceof Element) handleTarget(event.target, false);
    },
    { passive: true }
  );

  // Keyboard navigation: swap with no delay when a link receives focus.
  document.addEventListener('focusin', function (event) {
    if (event.target instanceof Element) handleTarget(event.target, true);
  });

  // Fall back to the resting banner once the pointer leaves the menu column.
  document.addEventListener(
    'pointerleave',
    function (event) {
      var container = event.target;

      if (!(container instanceof Element)) return;
      if (!container.hasAttribute('data-menu-list-id')) return;

      if (hoverTimer) clearTimeout(hoverTimer);

      var panel = getPanel(container);
      if (panel) activate(panel, panel.getAttribute('data-nav-preview-default'));
    },
    true
  );

  // Theme editor re-renders sections in place; drop stale panel references.
  document.addEventListener('shopify:section:load', function () {
    panelCache.clear();
  });
})();
